import { endpoints } from 'core/api/endpoints';
import { notifySessionExpired } from 'core/api/sessionEvents';
import { ApiError } from 'core/errors/ApiError';
import {
  clearAdminSession,
  getAccessToken,
  getRefreshToken,
  isAccessTokenExpired,
  isAccessTokenExpiringSoon,
  setSessionFromLogin,
} from 'auth/tokenStore';

let inflightRefresh = null;

function shouldAttemptRefresh({ force = false } = {}) {
  if (!getRefreshToken()) {
    return false;
  }
  if (force) {
    return true;
  }
  return isAccessTokenExpiringSoon() || isAccessTokenExpired();
}

async function requestRefresh(refreshToken) {
  const { apiPost } = await import('core/api/client');
  return apiPost(endpoints.refresh, { refresh_token: refreshToken }, {
    skipTokenRefresh: true,
    skipAuth: true,
    skipSessionExpire: true,
  });
}

async function performRefresh({ force = false } = {}) {
  if (!shouldAttemptRefresh({ force })) {
    return false;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  try {
    const data = await requestRefresh(refreshToken);
    if (data?.access_token) {
      setSessionFromLogin(data);
      return true;
    }
    return false;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      clearAdminSession();
      notifySessionExpired();
      return false;
    }
    throw error;
  }
}

export function getInflightRefresh() {
  return inflightRefresh;
}

export async function waitForInflightRefresh() {
  if (inflightRefresh) {
    await inflightRefresh;
  }
}

export async function refreshAccessToken({ force = false } = {}) {
  if (!hasRefreshableSession(force)) {
    return false;
  }

  if (!inflightRefresh) {
    inflightRefresh = performRefresh({ force }).finally(() => {
      inflightRefresh = null;
    });
  }

  return inflightRefresh;
}

function hasRefreshableSession(force) {
  if (getRefreshToken()) {
    return true;
  }
  if (!force) {
    return Boolean(getAccessToken()) && shouldAttemptRefresh({ force: false });
  }
  return Boolean(getAccessToken());
}

export async function refreshAccessTokenIfNeeded() {
  return refreshAccessToken();
}
